import { jsx as _jsx } from "react/jsx-runtime";
import { Suspense, forwardRef, lazy, useEffect, useImperativeHandle, useMemo, } from 'react';
import { ErrorBoundary } from '@servicetitan/error-boundary';
import { Log } from '@servicetitan/log-service';
import { useOptionalDependencies } from '@servicetitan/react-ioc';
import { EXPOSED_DEPENDENCIES_TOKEN, EXPOSED_INSTANCE_DEPENDENCIES_TOKEN, useCancelation, } from './common';
import { Delayed } from './delayed';
import { EVENT_BUS_TOKEN, useEventBus } from './event-bus';
import { EXPOSED_DEPENDENCIES, EXPOSED_INSTANCE_DEPENDENCIES } from './globals';
import { TtlCache, getBundleInfo, getLDServiceToken, validateBundleInfo, loadScripts, webComponent, } from './utils';
export const Loader = forwardRef((props, ref) => {
    var _a, _b;
    const { cache = true, basename, fallbackSrc, onRequestError, retries, src } = props;
    const { loadingFallback, loadingFallbackDelayed = true } = props;
    const bus = useEventBus();
    const [logService, exposedDependencies = EXPOSED_DEPENDENCIES, eventBus = bus] = useOptionalDependencies(Log, EXPOSED_DEPENDENCIES_TOKEN, EVENT_BUS_TOKEN);
    let [exposedInstanceDependencies = EXPOSED_INSTANCE_DEPENDENCIES] = useOptionalDependencies(EXPOSED_INSTANCE_DEPENDENCIES_TOKEN);
    const [ldService] = useOptionalDependencies((_a = getLDServiceToken()) !== null && _a !== void 0 ? _a : Symbol());
    const cancelation = useCancelation();
    const LoadingFallback = useMemo(() => {
        const message = `Loading${isLocalHost(src) ? ` ${src}` : ''}...`;
        return getFallback(loadingFallback, message, { delayed: loadingFallbackDelayed });
    }, [loadingFallback, loadingFallbackDelayed, src]);
    const ErrorFallback = useMemo(() => {
        const message = `Something went wrong${isLocalHost(src) ? ` loading ${src}` : ''}`;
        return getFallback(props.errorFallback, message);
    }, [props.errorFallback, src]);
    exposedInstanceDependencies = useMemo(() => ensureLDService(exposedInstanceDependencies, ldService), [exposedInstanceDependencies, ldService]);
    const Loader = useMemo(() => lazyLoader({
        basename,
        cache,
        cancelation,
        exposedDependencies,
        exposedInstanceDependencies,
        eventBus,
        fallbackSrc,
        logService,
        onRequestError,
        retries,
        src,
    }), [
        basename,
        cache,
        cancelation,
        exposedDependencies,
        exposedInstanceDependencies,
        eventBus,
        logService,
        onRequestError,
        fallbackSrc,
        retries,
        src,
    ]);
    useImperativeHandle(ref, () => eventBus, [eventBus]);
    return (_jsx(ErrorBoundary, { moduleName: src, fallback: ErrorFallback, children: _jsx(Suspense, { fallback: _jsx(LoadingFallback, {}), children: _jsx(Loader, { data: (_b = props.data) !== null && _b !== void 0 ? _b : {}, className: props.className, LoadingFallback: LoadingFallback }) }) }));
});
function lazyLoader(props) {
    const { cancelation } = props;
    const LazyWebComponent = lazyWebComponent(props);
    return (props) => {
        useEffect(() => {
            return () => {
                cancelation.abort();
            };
        }, []);
        return (_jsx(LazyWebComponent, { data: props.data, className: props.className, LoadingFallback: props.LoadingFallback }));
    };
}
function lazyWebComponent(props) {
    const { basename, cancelation, exposedDependencies, exposedInstanceDependencies, eventBus, fallbackSrc, logService, onRequestError, retries, src, } = props;
    let { cache } = props;
    return lazy(async () => {
        var _a;
        let bundleInfo = cache ? bundleCache.get(src) : undefined;
        if (!bundleInfo) {
            try {
                bundleInfo = await getBundleInfo({
                    mainPackageUrl: src,
                    fallbackPackageUrl: fallbackSrc,
                    exposedDependencies,
                    exposedInstanceDependencies,
                    signal: cancelation.signal,
                    retries,
                    onRequestError,
                });
            }
            catch (e) {
                if (cancelation.signal.aborted) {
                    return {
                        default: () => null,
                    };
                }
                throw e;
            }
            if (cache === true && !bundleInfo.mfeHasUniqueChunkNamespace) {
                cache = -1;
            }
            if (cache) {
                bundleCache.set(src, bundleInfo, typeof cache === 'number' ? cache : undefined);
            }
            validateBundleInfo(bundleInfo, {
                exposedDependencies,
                exposedInstanceDependencies,
                logService,
                packageUrl: src,
            });
        }
        await loadScripts(bundleInfo);
        const ldService = bundleInfo.mfeHasCompatibleLaunchDarklyVersion
            ? (_a = exposedInstanceDependencies === null || exposedInstanceDependencies === void 0 ? void 0 : exposedInstanceDependencies.launchDarkly) === null || _a === void 0 ? void 0 : _a.ldService
            : undefined;
        return {
            default: webComponent({
                basename,
                bundleInfo,
                eventBus,
                exposedDependencies,
                exposedInstanceDependencies,
                ldService,
                logService,
            }),
        };
    });
}
function getFallback(node, message, { delayed = false } = {}) {
    const fallbackNode = node !== undefined ? node : _jsx("h2", { children: message });
    return delayed ? () => _jsx(Delayed, { children: fallbackNode }) : () => fallbackNode;
}
function isLocalHost(source) {
    return ['localhost', '127.0.0.1'].some(host => source.indexOf(host) >= 0);
}
function ensureLDService(exposedInstanceDependencies, ldService) {
    const launchDarkly = exposedInstanceDependencies === null || exposedInstanceDependencies === void 0 ? void 0 : exposedInstanceDependencies.launchDarkly;
    if ((launchDarkly === null || launchDarkly === void 0 ? void 0 : launchDarkly.version) && !launchDarkly.ldService && ldService) {
        return { ...exposedInstanceDependencies, launchDarkly: { ...launchDarkly, ldService } };
    }
    return exposedInstanceDependencies;
}
const MINUTE = 1000 * 60;
const bundleCache = new TtlCache({ ttlMs: 15 * MINUTE });
//# sourceMappingURL=loader.js.map